/**
 * Consolidated analytics events and tracking functionality
 * This file combines core events (shared with mobile) and web-specific events
 */
import {
  trackPostHogEvent,
  identifyUser,
  resetUser,
  isFeatureEnabled,
  getFeatureFlag,
  getFeatureFlagPayload,
  groupCompany,
  captureException,
} from "./posthog";
import logger from "./logger";


/**
 * Constants for all analytics event names
 * Core events are shared with the mobile app for consistent tracking
 */
export const AnalyticsEvents = {
  // App lifecycle events
  APP_STARTUP: "app_startup",
  APP_BACKGROUNDED: "app_backgrounded",
  APP_FOREGROUNDED: "app_foregrounded",

  // User account events
  LOGIN_ATTEMPTED: "login_attempted",
  USER_LOGIN: "login_success",
  USER_LOGIN_FAILED: "login_failure",
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  SIGNUP_FAILED: "signup_failed",
  USER_LOGOUT: "user_logout",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_EMAIL_SENT: "password_reset_email_sent",
  PASSWORD_RESET_FAILED: "password_reset_failed",
  DELETE_ACCOUNT_INITIATED: "delete_account_initiated",
  DELETE_ACCOUNT_SUCCESS: "delete_account_success",
  DELETE_ACCOUNT_FAILED: "delete_account_failed",

  // Search events
  SEARCH_PERFORMED: "search_performed",
  SEARCH_SUBMITTED: "search_submitted",
  SEARCH_ZERO_RESULTS: "search_zero_results",
  SEARCH_VIEW_MODE_CHANGED: "search_view_mode_changed",
  FILTER_APPLIED: "filter_applied",
  SEARCH_RESULT_VIEWED: "search_result_viewed",

  // Listing events
  LISTING_VIEWED: "listing_viewed",
  LISTING_OPENED: "listing_opened",
  LISTING_SAVED: "listing_saved",
  LISTING_SHARED: "listing_shared",

  // Booking events
  DIRECT_BOOKING_INITIATED: "direct_booking_initiated",
  BOOK_FOR_OTHERS_CLICKED: "book_for_others_clicked",
  BOOK_FOR_OTHERS_USER_ADDED: "book_for_others_user_added",
  BOOK_FOR_OTHERS_USER_REMOVED: "book_for_others_user_removed",
  BOOKING_INITIATED: "booking_initiated",
  BOOKING_STEP_COMPLETED: "booking_step_completed",
  PAYMENT_METHOD_SELECTED: "payment_method_selected",
  BOOKING_COMPLETED: "booking_completed",
  BOOKING_FAILED: "booking_failed",
  BOOKING_ABANDONED: "booking_abandoned",

  // Add-on events
  ADDON_SELECTED: "addon_selected",
  ADDON_DESELECTED: "addon_deselected",
  ADDON_QUANTITY_CHANGED: "addon_quantity_changed",
  ADDON_DURATION_CHANGED: "addon_duration_changed",
  ADDON_COMPATIBILITY_WARNING_VIEWED: "addon_compatibility_warning_viewed",
  ADDON_DATE_SWITCHED: "addon_date_switched",

  // Profile & Settings events
  PROFILE_ACTION: "profile_action",
  PROFILE_EDITED: "profile_edited",
  SETTINGS_SECTION_VIEWED: "settings_section_viewed",
  SETTINGS_SECTION_CLICKED: "settings_section_clicked",
  SETTINGS_ACTION: "settings_action",
  PROFILE_UPDATED: "profile_updated",
  PASSWORD_CHANGED: "password_changed",
  SECURITY_SETTINGS_UPDATED: "security_settings_updated",
  COMPANY_PROFILE_UPDATED: "company_profile_updated",
  NOTIFICATIONS_SETTINGS_UPDATED: "notifications_settings_updated",
  SETTINGS_ERROR: "settings_error",

  // API events
  API_SUCCESS: "api_success",
  API_FAILURE: "api_failure",
  API_EXPLORER_OPENED: "api_explorer_opened",
  API_CALL_EXECUTED: "api_call_executed",
  API_RESPONSE_RECEIVED: "api_response_received",

  // Payment events
  PAYMENT_INITIATED: "payment_initiated",
  PAYMENT_COMPLETED: "payment_completed",
  PAYMENT_FAILED: "payment_failed",
  INVOICE_VIEWED: "invoice_viewed",
  INVOICE_DOWNLOADED: "invoice_downloaded",
  STATEMENT_VIEWED: "statement_viewed",
  STATEMENT_DOWNLOADED: "statement_downloaded",
  PACKAGE_PAYMENT_INITIATED: "package_payment_initiated",
  PACKAGE_PAYMENT_COMPLETED: "package_payment_completed",
  PACKAGE_PAYMENT_FAILED: "package_payment_failed",
  WEB_PAYMENT_INITIATED: "web_payment_initiated",
  WEB_PAYMENT_COMPLETED: "web_payment_completed",
  WEB_PAYMENT_FAILED: "web_payment_failed",

  // Property Management events
  ADD_PROPERTY_STEP_NEXT: "add_property_step_next",
  ADD_PROPERTY_STEP_BACK: "add_property_step_back",
  ADD_PROPERTY_SUBMIT_TAPPED: "add_property_submit_tapped",
  ADD_PROPERTY_SUBMITTED: "add_property_submitted",
  ADD_PROPERTY_SUCCESS: "add_property_success",
  ADD_PROPERTY_FAILED: "add_property_failed",
  PROPERTY_IMAGE_UPLOADED: "property_image_uploaded",
  PROPERTY_IMAGE_UPLOAD_FAILED: "property_image_upload_failed",

  // User Management events
  MANAGE_USERS_INVITE_ATTEMPT: "manage_users_invite_attempt",
  MANAGE_USERS_INVITE_SUCCESS: "manage_users_invite_success",
  MANAGE_USERS_INVITE_FAILED: "manage_users_invite_failed",
  USER_ROLE_SELECTED: "user_role_selected",
  USER_ENABLE_INITIATED: "user_enable_initiated",
  USER_DISABLE_INITIATED: "user_disable_initiated",
  USER_ENABLED: "user_enabled",
  USER_DISABLED: "user_disabled",

  // Team Management Events
  CREATE_TEAM_ATTEMPT: "create_team_attempt",
  CREATE_TEAM_SUCCESS: "create_team_success",
  CREATE_TEAM_FAILED: "create_team_failed",
  UPDATE_TEAM_NAME_ATTEMPT: "update_team_name_attempt",
  UPDATE_TEAM_NAME_SUCCESS: "update_team_name_success",
  UPDATE_TEAM_NAME_FAILED: "update_team_name_failed",
  DELETE_TEAM_ATTEMPT: "delete_team_attempt",
  DELETE_TEAM_SUCCESS: "delete_team_success",
  DELETE_TEAM_FAILED: "delete_team_failed",
  ADD_MEMBER_TO_TEAM_ATTEMPT: "add_member_to_team_attempt",
  ADD_MEMBER_TO_TEAM_SUCCESS: "add_member_to_team_success",
  ADD_MEMBER_TO_TEAM_FAILED: "add_member_to_team_failed",
  REMOVE_MEMBER_FROM_TEAM_ATTEMPT: "remove_member_from_team_attempt",
  REMOVE_MEMBER_FROM_TEAM_SUCCESS: "remove_member_from_team_success",
  REMOVE_MEMBER_FROM_TEAM_FAILED: "remove_member_from_team_failed",

  // Booking web-specific
  BOOKING_CALENDAR_VIEWED: "booking_calendar_viewed",
  DATE_RANGE_CHANGED: "date_range_changed",
  CAPACITY_CHANGED: "capacity_changed",
  BOOKING_MODAL_OPENED: "booking_modal_opened",
  HOST_CREATE_BOOKING_CLICKED: "host_create_booking_clicked",
};

/**
 * Constants for all analytics property names
 * Core properties are shared with the mobile app for consistency
 */
export const AnalyticsProps = {
  // Common properties
  FLAVOR: "flavor",
  PLATFORM: "platform",
  APP_VERSION: "app_version",
  APP_TITLE: "app_title",
  IS_FIRST_LAUNCH: "is_first_launch",
  IS_LOGGED_IN: "is_logged_in",
  PAGE_NAME: "page",
  MODAL_NAME: "modal_name",
  INTERACTION_TYPE: "interaction_type",

  // User properties
  USER_ROLE: "user_role",
  LOGIN_METHOD: "login_method",
  SIGNUP_METHOD: "signup_method",

  // Search properties
  LOCATION: "location",
  DATE_RANGE: "date_range",
  SEARCH_RESULTS_COUNT: "results_count",
  NUM_FILTERS: "num_filters",
  FILTER_TYPES: "filter_types",
  PRICE_RANGE: "price_range",
  AMENITIES: "amenities",
  CAPACITY: "capacity",
  COST_MAX: "cost_max",
  AMENITIES_COUNT: "amenities_count",
  IS_AUTHENTICATED: "is_authenticated",

  // Listing properties
  LISTING_ID: "listing_id",
  PROPERTY_TYPE: "property_type",
  PRICE_PER_DAY: "price_per_day",
  VIEW_SOURCE: "view_source",
  LISTING_NAME: "listing_name",

  // Booking properties
  BOOKING_ID: "booking_id",
  START_DATE: "start_date",
  END_DATE: "end_date",
  TOTAL_DAYS: "total_days",
  ESTIMATED_PRICE: "estimated_price",
  TOTAL_AMOUNT: "total_amount",
  CURRENCY: "currency",
  BOOKING_FOR_OTHERS: "booking_for_others",
  BOOKING_METHOD: "booking_method",
  BOOKING_STEP: "booking_step",
  BOOKING_TYPE: "booking_type",
  BOOKING_CONTEXT: "booking_context",
  PAYMENT_MODE: "payment_mode",
  IS_PLAY_SERVICE: "is_play_service",
  IS_OFFLINE_TICKET: "is_offline_ticket",
  BOOKING_COUNT: "booking_count",
  BOOKING_AMOUNT: "booking_amount",
  BOOKING_DURATION: "booking_duration",
  GROUP_ID: "group_id",
  MEMBER_COUNT: "member_count",
  ADDED_USER_ID: "added_user_id",
  SELECTED_COUNT: "selected_count",

  // Payment properties
  PAYMENT_METHOD: "payment_method",
  TRANSACTION_ID: "transaction_id",
  PAYMENT_PROVIDER: "payment_provider",
  ORDER_ID: "order_id",
  PRICE: "price",
  BILLING_PERIOD: "billing_period",
  INVOICE_ID: "invoice_id",
  STATEMENT_PERIOD: "statement_period",

  // Error properties
  ERROR_REASON: "error_reason",
  ERROR_MESSAGE: "error_message",

  // Profile properties
  PROFILE_ACTION: "profile_action",
  PROFILE_FIELD_CHANGED: "profile_field_changed",

  // Property Management properties
  IS_EDITING: "is_editing",
  PROPERTY_ID: "property_id",
  PROPERTY_ID_NEW: "property_id_new",
  ZONE: "zone",
  COUNTRY: "country",
  CURRENT_STEP_INDEX: "current_step_index",
  CURRENT_STEP_NAME: "current_step_name",
  IS_LAST_STEP: "is_last_step",

  // User Management properties
  INVITE_COUNT: "invite_count",
  ROLE_ASSIGNED: "role_assigned",
  USER_EMAIL: "user_email",
  USER_STATUS: "user_status",

  // Settings properties
  SETTINGS_SECTION: "settings_section",
  ACTION_TYPE: "action_type",
  FIELD_CHANGED: "field_changed",
  SETTING_NAME: "setting_name",
  SETTING_VALUE: "setting_value",
  COMPONENT_NAME: "component_name",
  IS_SUCCESS: "is_success",
  PACKAGE_TYPE: "package_type",
  BUNDLE_QUANTITY: "bundle_quantity",
  MEMBERSHIP_LEVEL: "membership_level",

  // API Explorer properties
  ENDPOINT_NAME: "endpoint_name",
  ENDPOINT_METHOD: "endpoint_method",
  RESPONSE_STATUS: "response_status",

  // Form properties
  FORM_STEP: "form_step",
  FORM_VALIDATION_ERROR: "form_validation_error",

  // Web behavior properties
  SCROLL_DEPTH: "scroll_depth",
  TIME_ON_PAGE: "time_on_page",
  PAGE_RELOAD_COUNT: "page_reload_count",
};

/**
 * Helper function to track analytics events
 * Single tracking function to use across the application
 *
 * @param eventName Name of the event from AnalyticsEvents
 * @param props Properties to track with the event from AnalyticsProps
 */
export const trackAnalyticsEvent = (eventName: string, props: Record<string, any> = {}) => {
  try {
    trackPostHogEvent(eventName, props);
    logger.log(`[Analytics] Tracked event: ${eventName}`, props);
  } catch (error) {
    logger.error(`[Analytics] Error tracking event ${eventName}:`, error);
  }
};

export { identifyUser, resetUser, isFeatureEnabled, getFeatureFlag, getFeatureFlagPayload, groupCompany, captureException };

const Analytics = {
  Events: AnalyticsEvents,
  Props: AnalyticsProps,
  trackEvent: trackAnalyticsEvent,
  identifyUser,
  resetUser,
  groupCompany,
  captureException,
  isFeatureEnabled,
  getFeatureFlag,
  getFeatureFlagPayload,
};

export default Analytics;

